import { Navigate, Outlet, Route, Routes } from "react-router-dom";
import { useSessionStore } from "@/store/session";
import { FullScreenLoader } from "@/components/FullScreenLoader";
import { AdminLayout } from "@/pages/admin/AdminLayout";
import { UserDetailPage } from "@/pages/admin/UserDetailPage";
import { TextsAiScreen } from "@/pages/admin/screens/TextsAiScreen";
import { FeaturesScreen } from "@/pages/admin/screens/FeaturesScreen";
import { MoneyScreen } from "@/pages/admin/screens/MoneyScreen";
import { PeopleScreen } from "@/pages/admin/screens/PeopleScreen";
import { FormsScreen } from "@/pages/admin/screens/FormsScreen";
import { ServiceScreen } from "@/pages/admin/screens/ServiceScreen";
import { ADMIN_HOME, LEGACY_ADMIN_REDIRECTS } from "./legacyRedirects";
import { LoginPage } from "./LoginPage";
import { AuthCallbackPage } from "./AuthCallbackPage";

/** DUE-221 (Task 12): UX-only gate for the standalone browser-admin bundle.
 * Mirrors `RequireAdmin` in web/src/app/routes.tsx, but redirects to
 * `/login` (this bundle's magic-link screen) instead of `/home` — there is
 * no Telegram/iOS product surface to fall back to here. The server still
 * enforces auth via `require_admin`; this only prevents the dashboard
 * shell from rendering before a session exists. */
function RequireAdminWeb(): JSX.Element {
  const hydrated = useSessionStore((s) => s.hydrated);
  const user = useSessionStore((s) => s.user);
  if (!hydrated) return <FullScreenLoader />;
  if (!user || user.is_admin !== true) return <Navigate to="/login" replace />;
  return <Outlet />;
}

export function AdminRoutes(): JSX.Element {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth" element={<AuthCallbackPage />} />
      <Route element={<RequireAdminWeb />}>
        <Route element={<AdminLayout />}>
          {/* Identical /admin/* paths as web/src/app/routes.tsx so
              AdminTabs' absolute links keep working. */}
          <Route path="/admin" element={<Navigate to={ADMIN_HOME} replace />} />
          <Route path="/admin/texts-ai" element={<TextsAiScreen />} />
          <Route path="/admin/features" element={<FeaturesScreen />} />
          <Route path="/admin/money" element={<MoneyScreen />} />
          <Route path="/admin/people" element={<PeopleScreen />} />
          <Route path="/admin/forms" element={<FormsScreen />} />
          <Route path="/admin/service" element={<ServiceScreen />} />
          <Route path="/admin/users/:userId" element={<UserDetailPage />} />
          {LEGACY_ADMIN_REDIRECTS.map(([from, to]) => (
            <Route key={from} path={from} element={<Navigate to={to} replace />} />
          ))}
        </Route>
      </Route>
      <Route path="*" element={<Navigate to={ADMIN_HOME} replace />} />
    </Routes>
  );
}
